import { FEATURE_PERMISSION_MODES, FEATURE_PERMISSIONS } from './feature-permissions';

type Feature = keyof typeof FEATURE_PERMISSIONS & keyof typeof FEATURE_PERMISSION_MODES;

/** One shortcut of «الرئيسية»: a screen, what of it to open, and the grant that screen is opened with. */
export interface HomeCard {
  readonly key: string;
  readonly path: string;
  /** The address's query: a list of the screen, as `?kind=employee` opens the employees' pay. */
  readonly query?: Readonly<Record<string, string>>;
  readonly requirement: { readonly permissions: readonly string[]; readonly mode: 'all' | 'any' };
}

/** Where «الرئيسية» is. */
export const HOME_PATH = '/home';

function card(key: string, path: string, feature: Feature, query?: Readonly<Record<string, string>>): HomeCard {
  return { key, path, ...(query ? { query } : {}), requirement: { permissions: FEATURE_PERMISSIONS[feature], mode: FEATURE_PERMISSION_MODES[feature] } };
}

/**
 * A card that begins a piece of work -- it opens the form itself -- shown to
 * whoever may open the screen and do that work: every grant named, the
 * screen's and the one the form is saved with.
 */
function begin(key: string, path: string, permissions: readonly string[], query?: Readonly<Record<string, string>>): HomeCard {
  return { key, path, ...(query ? { query } : {}), requirement: { permissions, mode: 'all' } };
}

/**
 * «الرئيسية» (the owner, 2026-10-13: «عايز قايمه مختصرة احط فيها الحاجات
 * المهمه»): the screens the desk opens all day, as cards on the first page
 * after signing in, in the order the owner named them.
 *
 * Each card is shown to whoever may open its screen, by that screen's own
 * grant, so a card never leads to «ممنوع»; and whoever has none of them never
 * lands on an empty page. «المرتبات الثابتة» (2026-10-13) opens «المرتبات»
 * on the employees' list. «فاتورة مشتريات جديدة» and «مصروف جديد» (the owner,
 * 2026-10-15: «محتاج اضيف اضافة فاتورة مشتريات او اضافة مصاريف في الالقائمة
 * المختصرة») open the new invoice and the new expense, for whoever may add
 * them: reading the invoices or the expenses is not enough for these two.
 */
export const HOME_CARDS: readonly HomeCard[] = [
  card('registerPatient', '/patients/register', 'patientRegistration'),
  card('inCenter', '/admissions/in-center', 'admissionsInCenter'),
  // «الخزن» (the owner, 2026-10-21: «ممكن نضيف الخزن للرئيسية»).
  card('cashTills', '/finance/cash-tills', 'cashTills'),
  card('attendance', '/staff/attendance', 'staffAttendance'),
  card('payroll', '/staff/payroll', 'staffPayroll'),
  card('rounds', '/staff/rounds', 'consultantRounds'),
  card('doctorWork', '/staff/doctor-work', 'doctorWorkSheet'),
  card('providers', '/finance/external-providers', 'externalProviders'),
  card('salaries', '/staff/payroll', 'staffPayroll', { kind: 'employee' }),
  begin('newPurchaseInvoice', '/purchasing/invoices/new', [...FEATURE_PERMISSIONS.purchaseInvoices, 'purchasing.invoices.manage']),
  begin('newExpense', '/finance/expenses', ['expenses.create'], { create: '1' }),
];
